import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Grid,
  Typography,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  FormHelperText,
} from '@mui/material';
import PropTypes from 'prop-types';
import { CiFilter } from 'react-icons/ci';
import { Search, Clear } from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { getLabelsByInstituteType } from '../../utils/constants';
import { MenuProps } from '../../utils/constants';
// import { useDispatch } from 'react-redux';
// import { getExaminationsList } from '../../features/examinations/examinationSlice';
// import { getExamSchedulesList } from '../../features/schedule/examScheduleSlice';

const FilterStudents = ({
  initialFilters = {
    examType: '',
    department: 'Select',
    section: 'Select',
    class: 'Select',
  },
  onFiltersChange,
  onSearch,
  onClearFilters,
  examTypes = [],
  departments = [],
}) => {
  //const dispatch = useDispatch();
  const [filters, setFilters] = useState(initialFilters);
  const [availableClasses, setAvailableClasses] = useState(['Select']);
  const [availableSections, setAvailableSections] = useState(['Select']);
  const [validationErrors, setValidationErrors] = useState({});

  const { instituteType } = useSelector((state) => {
    return state.auth?.successData?.userDetails ?? {};
  });
  let { Department, Class, Section } = getLabelsByInstituteType(instituteType);

  // Prepare department options
  const departmentOptions = [
    'Select',
    ...departments.map((d) => d.department || d),
  ];

  // Update classes when department changes
  useEffect(() => {
    if (filters.department !== 'Select') {
      // Show classes for selected department only
      const dept = departments.find(
        (d) => (d.department || d) === filters.department,
      );
      const deptClasses = dept?.classes || [];
      setAvailableClasses([
        'Select',
        ...deptClasses.map((c) => c.className || c),
      ]);
    } else {
      setAvailableClasses(['Select']);
    }

    // Reset class filter when department changes
    if (filters.class !== 'Select') {
      setFilters((prev) => ({ ...prev, class: 'Select' }));
    }
  }, [filters.department, departments]);

  // Update sections when class changes
  useEffect(() => {
    if (filters.class === 'Select' || filters.department === 'Select') {
      setAvailableSections(['Select']);
    } else {
      // Find the selected class and get its sections
      const dept = departments.find(
        (d) => (d.department || d) === filters.department,
      );
      const selectedClass = dept?.classes?.find(
        (c) => (c.className || c) === filters.class,
      );
      const classSections = selectedClass?.sections || []; //This is an array of objects
      const filteredSections = classSections?.map((obj) => obj.section);
      setAvailableSections(['Select', ...filteredSections]);
    }

    // Reset section filter when class changes
    if (filters.section !== 'Select') {
      setFilters((prev) => ({ ...prev, section: 'Select' }));
    }
  }, [filters.class, filters.department, departments]);

  // Notify parent component when filters change
  useEffect(() => {
    if (onFiltersChange) {
      onFiltersChange(filters);
    }
  }, [filters, onFiltersChange]);

  const handleChange = (field, value) => {
    setFilters((prev) => {
      const newFilters = { ...prev, [field]: value };

      // Clear validation error for this field when user selects a value
      if (value !== 'Select') {
        setValidationErrors((prevErrors) => {
          const newErrors = { ...prevErrors };
          delete newErrors[field];
          return newErrors;
        });
      }

      return newFilters;
    });
  };

  // Validation function
  const validateFilters = () => {
    const errors = {};

    // Check required fields
    if (
      !filters.examType ||
      filters.examType === 'Select' ||
      filters.examType === ''
    ) {
      errors.examType = 'Please select an exam type';
    }

    if (
      !filters.department ||
      filters.department === 'Select' ||
      filters.department === 'Select Departments'
    ) {
      errors.department = 'Please select a department';
    }

    if (
      !filters.class ||
      filters.class === 'Select' ||
      filters.class === 'Select Classes'
    ) {
      errors.class = 'Please select a class';
    }

    // Section is mandatory for college institution type
    if (instituteType?.toLowerCase() === 'college') {
      if (
        !filters.section ||
        filters.section === 'Select' ||
        filters.section === 'Select Sections'
      ) {
        errors.section = 'Please select a section';
      }
    }

    return errors;
  };

  const handleSearch = () => {
    // Validate filters before searching
    const errors = validateFilters();

    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }

    // Clear any existing validation errors
    setValidationErrors({});

    if (onSearch) {
      onSearch({
        ...filters,
        instituteType: instituteType,
        section:
          instituteType?.toLowerCase() === 'college' ? filters.section : '',
      });
    }
  };

  const handleClearFilters = () => {
    const clearedFilters = {
      examType: 'Select',
      department: 'Select',
      section: 'Select',
      class: 'Select',
    };
    setFilters(clearedFilters);
    setValidationErrors({});

    if (onClearFilters) {
      onClearFilters(clearedFilters);
    }
  };

  return (
    <Card className='mb-4'>
      <CardContent>
        <Box className='flex items-center gap-2 mb-4'>
          <Box className='size-8 bg-purple-500 rounded-full flex items-center justify-center'>
            <Typography className='text-white font-bold text-sm'>
              <CiFilter className='size-5' />
            </Typography>
          </Box>
          <Typography variant='h6' className='text-purple-700 font-semibold'>
            Filter Students
          </Typography>
        </Box>
        <Grid container spacing={3} alignItems={'start'}>
          {/* Exam Type */}
          <Grid item xs={12} md={2.4}>
            <FormControl
              fullWidth
              size='small'
              error={!!validationErrors.examType}
              required
            >
              <InputLabel>Exam Type</InputLabel>
              <Select
                MenuProps={MenuProps}
                value={filters.examType}
                label='Exam Type'
                onChange={(e) => handleChange('examType', e.target.value)}
              >
                <MenuItem value='Select'>Select Exam Type</MenuItem>
                {examTypes.map((type) => (
                  <MenuItem key={type.id} value={type.examType}>
                    {type.examType}
                  </MenuItem>
                ))}
              </Select>
              {validationErrors.examType && (
                <FormHelperText>{validationErrors.examType}</FormHelperText>
              )}
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl
              fullWidth
              size='small'
              error={!!validationErrors.department}
              required
            >
              <InputLabel>{Department}</InputLabel>
              <Select
                MenuProps={MenuProps}
                value={filters.department}
                label={Department}
                onChange={(e) => handleChange('department', e.target.value)}
              >
                {departmentOptions.map((d) => (
                  <MenuItem key={d} value={d}>
                    {d === 'Select' ? `Select ${Department}` : d}
                  </MenuItem>
                ))}
              </Select>
              {validationErrors.department && (
                <FormHelperText>{validationErrors.department}</FormHelperText>
              )}
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl
              fullWidth
              size='small'
              error={!!validationErrors.class}
              required
            >
              <InputLabel>{Class}</InputLabel>
              <Select
                MenuProps={MenuProps}
                value={filters.class}
                label={Class}
                onChange={(e) => handleChange('class', e.target.value)}
              >
                {availableClasses.map((c) => (
                  <MenuItem key={c} value={c}>
                    {c === 'Select' ? `Select ${Class}` : c}
                  </MenuItem>
                ))}
              </Select>
              {validationErrors.class && (
                <FormHelperText>{validationErrors.class}</FormHelperText>
              )}
            </FormControl>
          </Grid>

          {instituteType?.toLowerCase() === 'college' && (
            <Grid item xs={12} md={2}>
              <FormControl
                fullWidth
                size='small'
                error={!!validationErrors.section}
                required
              >
                <InputLabel>{Section}</InputLabel>
                <Select
                  MenuProps={MenuProps}
                  value={filters.section}
                  label={Section}
                  onChange={(e) => handleChange('section', e.target.value)}
                >
                  {availableSections.map((s) => (
                    <MenuItem key={s} value={s}>
                      {s === 'Select' ? `Select ${Section}` : s}
                    </MenuItem>
                  ))}
                </Select>
                {validationErrors.section && (
                  <FormHelperText>{validationErrors.section}</FormHelperText>
                )}
              </FormControl>
            </Grid>
          )}

          {/* Action Buttons */}
          <Grid item xs={12} md={2.4}>
            <Box
              sx={{
                display: 'flex',
                gap: 1,
                height: '100%',
                alignItems: 'center',
              }}
            >
              <Button
                variant='contained'
                color='primary'
                startIcon={<Search />}
                onClick={handleSearch}
                sx={{
                  minWidth: 90,
                  backgroundColor:
                    Object.keys(validationErrors).length > 0
                      ? 'error.main'
                      : 'primary.main',
                  '&:hover': {
                    backgroundColor:
                      Object.keys(validationErrors).length > 0
                        ? 'error.dark'
                        : 'primary.dark',
                  },
                }}
              >
                Search
              </Button>
              <Button
                variant='outlined'
                color='secondary'
                startIcon={<Clear />}
                onClick={handleClearFilters}
                sx={{
                  minWidth: 80,
                }}
              >
                Clear
              </Button>
            </Box>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

FilterStudents.propTypes = {
  initialFilters: PropTypes.object,
  onFiltersChange: PropTypes.func,
  onSearch: PropTypes.func,
  onClearFilters: PropTypes.func,
};

export default FilterStudents;
